<template>
  <!--
    A2P 10DLC opt-in. Rendered only when the venue has SMS registration
    underway (see `enabled`), because a consent checkbox for a venue that
    cannot text is noise to the customer.

    Compliance constraints baked in here, do not "simplify" them away:
      - both boxes start unchecked and require an affirmative click
      - consent is never a condition of booking, stated in the hint
      - the label carries message type, frequency, rate disclosure, STOP/HELP,
        and links to policy pages
      - the label names BOTH the venue and the platform when they differ, so
        consent stays valid if sending ever moves to a platform-owned number

    Styling mirrors the custom-field checkboxes in CreateBooking.vue so the
    venue's theme colors apply: base-checkbox with border/accent/check-mark
    driven off the booking site theme, `simple size="sm"`, links in link_color.
  -->
  <div v-if="enabled" class="sms-consent">
    <base-checkbox
      simple
      size="sm"
      class="sms-consent__box"
      :border-color="theme.button_color"
      :accent-color="theme.button_color"
      :check-mark-color="theme.card_color"
      v-model="transactionalModel"
    >
      <span :style="labelStyle">{{ transactionalLabel }}</span>
      <span v-if="hasPolicyLinks" :style="labelStyle">
        {{ $t('smsConsent.seeOur') }}
        <a v-if="privacyUrl" :href="privacyUrl" target="_blank" rel="noopener" :style="linkStyle">{{ $t('smsConsent.privacyPolicy') }}</a>
        <template v-if="privacyUrl && termsUrl"> {{ $t('smsConsent.and') }} </template>
        <a v-if="termsUrl" :href="termsUrl" target="_blank" rel="noopener" :style="linkStyle">{{ $t('smsConsent.termsOfService') }}</a>.
      </span>
    </base-checkbox>

    <base-checkbox
      v-if="showMarketing"
      simple
      size="sm"
      class="sms-consent__box"
      :border-color="theme.button_color"
      :accent-color="theme.button_color"
      :check-mark-color="theme.card_color"
      v-model="marketingModel"
    >
      <span :style="labelStyle">{{ marketingLabel }}</span>
    </base-checkbox>

    <p class="sms-consent__hint" :style="hintStyle">
      {{ $t('smsConsent.notRequired') }}
    </p>
  </div>
</template>

<script>
import BaseCheckbox from '@/components/Inputs/BaseCheckbox.vue';

// Kept deliberately free of optional chaining / nullish coalescing: this repo's
// build does not transpile them.
export default {
  name: 'SMSConsent',
  components: { BaseCheckbox },
  props: {
    // Venue has SMS registration at least underway. Parent decides.
    enabled: { type: Boolean, default: false },
    // Legal business name as registered with the carrier. A mismatch between
    // this and the brand registration is what triggers Twilio error 30927 on
    // campaign review.
    businessName: { type: String, default: '' },
    platformName: { type: String, default: '' },
    privacyUrl: { type: String, default: '' },
    termsUrl: { type: String, default: '' },
    showMarketing: { type: Boolean, default: true },
    transactionalConsent: { type: Boolean, default: false },
    marketingConsent: { type: Boolean, default: false },
    theme: { type: Object, default: function () { return {}; } }
  },
  computed: {
    transactionalModel: {
      get: function () { return this.transactionalConsent; },
      set: function (value) {
        this.$emit('update:transactionalConsent', value);
        this.emitSnapshot();
      }
    },
    marketingModel: {
      get: function () { return this.marketingConsent; },
      set: function (value) {
        this.$emit('update:marketingConsent', value);
        this.emitSnapshot();
      }
    },
    senderName: function () {
      var venue = (this.businessName || '').trim();
      var platform = (this.platformName || '').trim();
      if (!venue) return platform;
      // The venue IS the platform operator (our own venue), or the two names
      // match: naming one party twice reads as a mistake to a customer and to
      // a campaign reviewer.
      if (!platform || venue.toLowerCase() === platform.toLowerCase()) return venue;
      return this.$t('smsConsent.senderPair', { business: venue, platform: platform });
    },
    transactionalLabel: function () {
      return this.$t('smsConsent.transactional', { sender: this.senderName });
    },
    marketingLabel: function () {
      return this.$t('smsConsent.marketing', { sender: this.senderName });
    },
    hasPolicyLinks: function () {
      return Boolean(this.privacyUrl) || Boolean(this.termsUrl);
    },
    labelStyle: function () {
      return {
        color: this.theme.text_color_card,
        'font-family': this.theme.font_family_card
      };
    },
    hintStyle: function () {
      return {
        color: this.theme.text_color_card,
        'font-family': this.theme.font_family_card
      };
    },
    linkStyle: function () {
      return {
        color: this.theme.link_color || this.theme.button_color,
        'font-family': this.theme.font_family_card
      };
    },
    // Verbatim text the customer saw, so the stored consent record can prove
    // what was agreed to rather than what today's build happens to render.
    consentTextSnapshot: function () {
      var suffix = '';
      if (this.privacyUrl) suffix += ' ' + this.privacyUrl;
      if (this.termsUrl) suffix += ' ' + this.termsUrl;
      return {
        transactional: this.transactionalLabel + suffix,
        marketing: this.showMarketing ? this.marketingLabel : null
      };
    }
  },
  methods: {
    emitSnapshot: function () {
      this.$emit('consent-text', this.consentTextSnapshot);
    }
  }
};
</script>

<style scoped>
.sms-consent {
  margin-top: 0.25rem;
}

.sms-consent__box {
  margin-bottom: 0.5rem;
}

.sms-consent__hint {
  font-size: 0.75rem;
  line-height: 1.4;
  opacity: 0.75;
  margin-bottom: 0;
}
</style>
